import { useCallback, useMemo } from "react";
import { TodoStatus as TodoStatusNode } from "@acto/gql/graphql";
import { useTodos } from "@acto/taskpane/core/data-access/todos/useTodos";
import { Todo } from "@acto/taskpane/core/domain";

export type TodoBucketFilter = "all" | "overdue" | "open" | "completed";

/**
 * Cross-customer Todos page data. The shared `todos` query is assignee-narrowed to the current
 * user across all clients, so this backs the top-level Todos page (unlike the client-scoped
 * `clientTodos`). There is no global triple-connection, so the three buckets are three independent
 * `useTodos` calls, each cursor-paginated on its own (see `hasNextPage`/`fetchNextPage`).
 *
 * All filtering is **server-side** and the three buckets are disjoint by construction:
 * - overdue → `showOverdue: true` (due < today — strictly past-due; today is NOT overdue)
 * - open    → `showOverdue: false` (due ≥ today OR no due date — the "not overdue" set)
 * - completed → `processedStatus: COMPLETED`
 *
 * The status filter gates which bucket queries run (`enabled`); the search term is passed as
 * `clientSearchTerm` (smart client search: name, city, postcode and customer number).
 */
export interface AllTodosBucket {
  todos: Todo[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
}

export interface AllTodosForPage {
  overdue: AllTodosBucket;
  open: AllTodosBucket;
  completed: AllTodosBucket;
  isPending: boolean;
  error?: Error;
  refetch: () => void;
}

interface UseAllTodosForPageOptions {
  searchTerm?: string;
  filter: TodoBucketFilter;
  limit?: number;
}

const EMPTY_TODOS: readonly Todo[] = [];

export function useAllTodosForPage({ searchTerm, filter, limit = 10 }: UseAllTodosForPageOptions): AllTodosForPage {
  const search = searchTerm && searchTerm.trim().length > 0 ? searchTerm.trim() : undefined;

  const overdue = useTodos({
    processedStatus: TodoStatusNode.Open,
    showOverdue: true,
    clientSearchTerm: search,
    enabled: filter === "all" || filter === "overdue",
    limit,
  });
  const open = useTodos({
    processedStatus: TodoStatusNode.Open,
    showOverdue: false,
    clientSearchTerm: search,
    enabled: filter === "all" || filter === "open",
    limit,
  });
  const completed = useTodos({
    processedStatus: TodoStatusNode.Completed,
    clientSearchTerm: search,
    enabled: filter === "all" || filter === "completed",
    limit,
  });

  // Memoise on the underlying `.data` references (stable across renders when the server result is
  // unchanged) so a consumer effect keyed on `overdue.todos`/`open.todos` fires only on real changes.
  const overdueTodos = useMemo(() => overdue.data ?? EMPTY_TODOS, [overdue.data]);
  const openTodos = useMemo(() => open.data ?? EMPTY_TODOS, [open.data]);
  const completedTodos = useMemo(() => completed.data ?? EMPTY_TODOS, [completed.data]);

  const refetch = useCallback(() => {
    overdue.refetch?.();
    open.refetch?.();
    completed.refetch?.();
  }, [overdue.refetch, open.refetch, completed.refetch]);

  return {
    overdue: {
      todos: overdueTodos as Todo[],
      hasNextPage: overdue.hasNextPage,
      fetchNextPage: overdue.fetchNextPage,
      isFetching: overdue.isFetching,
    },
    open: {
      todos: openTodos as Todo[],
      hasNextPage: open.hasNextPage,
      fetchNextPage: open.fetchNextPage,
      isFetching: open.isFetching,
    },
    completed: {
      todos: completedTodos as Todo[],
      hasNextPage: completed.hasNextPage,
      fetchNextPage: completed.fetchNextPage,
      isFetching: completed.isFetching,
    },
    isPending: overdue.isPending || open.isPending || completed.isPending,
    error: overdue.error ?? open.error ?? completed.error,
    refetch,
  };
}
